// MediaSelectDialog.js
import React, { useContext } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Card,
  CardContent,
  Typography,
  IconButton,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  CameraAlt as CameraIcon,
  MovieCreation as MovieIcon,
  Mic as MicIcon,
  Close as CloseIcon,
  Image as ImageIcon,
} from '@mui/icons-material';
import { useTranslation } from "react-i18next";
import { LoginInfo } from '../../App';
const MediaSelectDialog = ({ open, onClose, onSelectSimpleImage, onSelectImage, onSelectVideo, onSelectAudio }) => {
  const {t} = useTranslation()
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // ログイン情報
  const {loginInfo,setLoginInfo} = useContext(LoginInfo)
  const handleImageSelect = () => {
    onSelectImage?.();
    onClose();
  };
  const handleVideoSelect = () => {
    onSelectVideo?.();
    onClose();
  };
  const handleSimpleImageSelect = () => {
    onSelectSimpleImage?.();
    onClose();
  };
  const handleAudioSelect = () => {
    onSelectAudio?.();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 2,
          boxShadow: 24,
        }
      }}
    >
      <DialogTitle sx={{ 
        m: 0, 
        p: 2, 
        pr: 5, 
        bgcolor: 'background.default',
        borderBottom: '1px solid',
        borderColor: 'divider'
      }}>
        <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
          🎨 メディア選択
        </Typography>          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
              '&:hover': {
                bgcolor: 'action.hover',
              },
            }}
          >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 2 }}>
        <Typography 
          variant="body2" 
          color="text.secondary" 
          sx={{ 
            mb: 2, 
            textAlign: 'center'
          }}
        >
          AIでメディアコンテンツを生成
        </Typography>

        <Grid container spacing={2}>
          {/* メイン生成機能 */}
          <Grid item xs={12} sm={6} md={4}>
            <Card
              variant="outlined"
              sx={{
                cursor: 'pointer',
                height: '100%',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  borderColor: 'primary.main',
                  bgcolor: 'action.hover',
                  transform: 'translateY(-2px)',
                  boxShadow: 2,
                },
              }}
              onClick={handleImageSelect}
            >
              <CardContent sx={{ textAlign: 'center', p: 2, height: '100%'}}>
                <CameraIcon
                  color="primary"
                  sx={{ fontSize: 32, mb: 1 }}
                />
                <Typography variant="subtitle1" component="div" gutterBottom>
                  画像
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  写真や絵を生成
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <Card
              variant="outlined"
              sx={{
                cursor: 'pointer',
                height: '100%',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  borderColor: 'primary.main',
                  bgcolor: 'action.hover',
                  transform: 'translateY(-2px)',
                  boxShadow: 2,
                },
              }}
              onClick={handleVideoSelect}
            >
              <CardContent sx={{ textAlign: 'center', p: 2, height: '100%'}}>
                <MovieIcon
                  color="primary"
                  sx={{ fontSize: 32, mb: 1 }}
                />
                <Typography variant="subtitle1" component="div" gutterBottom>
                  動画
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  ショート動画を生成
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <Card
              variant="outlined"
              sx={{
                cursor: 'pointer',
                height: '100%',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  borderColor: 'primary.main',
                  bgcolor: 'action.hover',
                  transform: 'translateY(-2px)',
                  boxShadow: 2,
                },
              }}
              onClick={handleAudioSelect}
            >
              <CardContent sx={{ textAlign: 'center', p: 2, height: '100%'}}>
                <MicIcon
                  color="primary"
                  sx={{ fontSize: 32, mb: 1 }}
                />
                <Typography variant="subtitle1" component="div" gutterBottom>
                  音声
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  音声を生成
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* 簡易版機能 */}
          <Grid item xs={12}>
            <Card
              variant="outlined"
              sx={{
                cursor: 'pointer',
                transition: 'all 0.2s ease-in-out',
                border: '2px dashed',
                borderColor: 'divider',
                '&:hover': {
                  borderColor: 'primary.main',
                  bgcolor: 'action.hover',
                  transform: 'translateY(-2px)',
                  boxShadow: 2,
                },
              }}
              onClick={handleSimpleImageSelect}
            >
              <CardContent sx={{ textAlign: 'center', p: 2 }}>
                <ImageIcon
                  color="primary"
                  sx={{ fontSize: 32, mb: 1 }}
                />
                <Typography variant="subtitle1" component="div" gutterBottom>
                  画像生成（簡易版）
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
                  シンプルな画面で手軽に画像生成
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ 
                  display: 'block',
                  bgcolor: 'background.paper',
                  p: 0.5,
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                  fontSize: '0.7rem'
                }}>
                  📝 文字生成　🎨 彩色　✂️ 背景削除　🤸 ポーズ指定
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ 
        px: 2, 
        pb: 2, 
        pt: 1,
        borderTop: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.default'
      }}>
        <Button 
          onClick={onClose} 
          variant="outlined"
          size="small"
          sx={{ minWidth: 80 }}
        >
          キャンセル
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MediaSelectDialog;